import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../components/AdminHeader';
import { getAbout, updateAbout, uploadImage } from '../../services/api';
import { toast } from 'sonner';
import { Upload, Loader, Save, ArrowLeft, X, Plus, FileText } from 'lucide-react';

const AdminAboutEdit = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    image_url: '',
    additional_images: [],
    highlights: [],
    quote: '',
    resume_url: ''
  });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newHighlight, setNewHighlight] = useState('');

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const data = await getAbout();
      setFormData({
        title: data.title || '',
        content: data.content || '',
        image_url: data.image_url || '',
        additional_images: data.additional_images || [],
        highlights: data.highlights || [],
        quote: data.quote || '',
        resume_url: data.resume_url || ''
      });
    } catch (error) {
      console.error('Error fetching about:', error);
      toast.error('Failed to load about content');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadImage(file);
      setFormData({ ...formData, image_url: response.file_url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleAdditionalImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingAdditional(true);
    try {
      const response = await uploadImage(file);
      const newImages = [...formData.additional_images, response.file_url].filter(Boolean);
      setFormData({
        ...formData,
        additional_images: newImages
      });
      toast.success('Additional image uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingAdditional(false);
    }
  };

  const handleRemoveAdditionalImage = (index) => {
    const newImages = formData.additional_images.filter((_, i) => i !== index);
    setFormData({ ...formData, additional_images: newImages });
  };

  const handleAddHighlight = () => {
    if (!newHighlight.trim()) return;
    setFormData({
      ...formData,
      highlights: [...formData.highlights, newHighlight.trim()]
    });
    setNewHighlight('');
  };

  const handleRemoveHighlight = (index) => {
    const newHighlights = formData.highlights.filter((_, i) => i !== index);
    setFormData({ ...formData, highlights: newHighlights });
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.includes('pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }

    setUploadingResume(true);
    try {
      const response = await uploadImage(file);
      if (response?.file_url) {
        setFormData({ ...formData, resume_url: response.file_url });
      }
      toast.success('Resume uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload resume');
    } finally {
      setUploadingResume(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        additional_images: formData.additional_images || [],
        highlights: formData.highlights || [],
        resume_url: formData.resume_url || ''
      };
      console.log('=== SUBMITTING ABOUT DATA ===');
      console.log('submitData:', JSON.stringify(submitData, null, 2));
      console.log('highlights:', submitData.highlights);
      console.log('additional_images:', submitData.additional_images);
      console.log('quote:', submitData.quote);
      console.log('resume_url:', submitData.resume_url);
      console.log('============================');

      console.log('About to call updateAbout...');
      const response = await updateAbout(submitData);
      console.log('updateAbout response:', response);
      toast.success('About page updated successfully!');
      console.log('About to navigate to /admin');
      navigate('/admin');
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to update about page');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
        <AdminHeader />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400 text-lg">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0f] via-[#1a1a2e] to-[#0a0a0f]">
      <AdminHeader />

      <main className="max-w-[1000px] mx-auto px-8 py-12">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 group"
        >
          <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">BACK</span>
        </button>

        <h1 className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-purple-600 font-['Poppins'] text-5xl font-black mb-8">
          EDIT ABOUT PAGE
        </h1>

        <form onSubmit={handleSubmit} className="glass-effect rounded-2xl p-8 space-y-6 border border-white/10">
          {/* Title */}
          <div>
            <label className="block text-white font-['Poppins'] text-xl font-semibold mb-2">
              PAGE TITLE *
            </label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              placeholder="e.g., About Charlie"
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all text-lg"
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-white font-['Poppins'] text-xl font-semibold mb-2">
              PROFILE IMAGE (OPTIONAL)
            </label>
            <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center bg-white/5">
              {formData.image_url ? (
                <div className="space-y-4">
                  <img
                    src={formData.image_url}
                    alt="Preview"
                    className="max-h-48 mx-auto rounded-xl border-2 border-purple-500"
                  />
                  <p className="text-green-400 font-semibold">✓ Image uploaded</p>
                </div>
              ) : (
                <div>
                  <Upload className="mx-auto text-gray-500 mb-4" size={48} />
                  <p className="text-gray-400 mb-4">Upload profile image</p>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="image-upload"
                disabled={uploading}
              />
              <label
                htmlFor="image-upload"
                className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg cursor-pointer hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 font-medium shadow-lg shadow-purple-500/30"
              >
                {uploading ? 'UPLOADING...' : 'SELECT IMAGE'}
              </label>
            </div>
            <p className="text-sm text-gray-400 mt-2">Or paste image URL:</p>
            <input
              type="url"
              name="image_url"
              value={formData.image_url}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all mt-2"
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-white font-['Poppins'] text-xl font-semibold mb-2">
              ABOUT CONTENT *
            </label>
            <textarea
              name="content"
              value={formData.content}
              onChange={handleChange}
              required
              rows="12"
              placeholder="Write about yourself..."
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all text-lg resize-none"
            />
          </div>

          {/* Additional Images */}
          <div>
            <label className="block text-white font-['Poppins'] text-xl font-semibold mb-2">
              ADDITIONAL IMAGES (OPTIONAL)
            </label>
            <p className="text-sm text-gray-400 mb-4">These images will appear throughout the article text</p>

            {formData.additional_images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                {formData.additional_images.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={img}
                      alt={`Additional ${index + 1}`}
                      className="w-full h-32 object-cover rounded-lg border border-white/10"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveAdditionalImage(index)}
                      className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              type="file"
              accept="image/*"
              onChange={handleAdditionalImageUpload}
              className="hidden"
              id="additional-image-upload"
              disabled={uploadingAdditional}
            />
            <label
              htmlFor="additional-image-upload"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg cursor-pointer hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 font-medium shadow-lg shadow-purple-500/30"
            >
              <Plus size={20} />
              {uploadingAdditional ? 'UPLOADING...' : 'ADD IMAGE'}
            </label>
          </div>

          {/* Highlights/Expertise */}
          <div>
            <label className="block text-white font-['Poppins'] text-xl font-semibold mb-2">
              HIGHLIGHTS / EXPERTISE (OPTIONAL)
            </label>
            <p className="text-sm text-gray-400 mb-4">Add skills or key highlights to display</p>

            {formData.highlights.length > 0 && (
              <div className="space-y-2 mb-4">
                {formData.highlights.map((highlight, index) => (
                  <div key={index} className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg p-3">
                    <span className="flex-1 text-gray-200">{highlight}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveHighlight(index)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                    >
                      <X size={18} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <input
                type="text"
                value={newHighlight}
                onChange={(e) => setNewHighlight(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddHighlight())}
                placeholder="e.g., Adobe Premiere Pro Expert"
                className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all"
              />
              <button
                type="button"
                onClick={handleAddHighlight}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all font-medium shadow-lg shadow-purple-500/30"
              >
                <Plus size={20} />
              </button>
            </div>
          </div>

          {/* Quote */}
          <div>
            <label className="block text-white font-['Poppins'] text-xl font-semibold mb-2">
              PERSONAL QUOTE (OPTIONAL)
            </label>
            <textarea
              name="quote"
              value={formData.quote}
              onChange={handleChange}
              rows="3"
              placeholder="A personal motto or favorite quote..."
              className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all text-lg resize-none"
            />
          </div>

          {/* Resume Upload */}
          <div>
            <label className="block text-white font-['Poppins'] text-xl font-semibold mb-2">
              RESUME (OPTIONAL)
            </label>
            <div className="border-2 border-dashed border-white/20 rounded-xl p-6 text-center bg-white/5">
              {formData.resume_url ? (
                <div className="space-y-4">
                  <FileText className="mx-auto text-green-400" size={48} />
                  <p className="text-green-400 font-semibold">Resume uploaded</p>
                  <a
                    href={formData.resume_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 underline"
                  >
                    View Resume
                  </a>
                </div>
              ) : (
                <div>
                  <FileText className="mx-auto text-gray-500 mb-4" size={48} />
                  <p className="text-gray-400 mb-4">Upload resume PDF</p>
                </div>
              )}
              <input
                type="file"
                accept=".pdf"
                onChange={handleResumeUpload}
                className="hidden"
                id="resume-upload"
                disabled={uploadingResume}
              />
              <label
                htmlFor="resume-upload"
                className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg cursor-pointer hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 font-medium shadow-lg shadow-purple-500/30"
              >
                {uploadingResume ? 'UPLOADING...' : 'SELECT PDF'}
              </label>
            </div>
            <p className="text-sm text-gray-400 mt-2">Or paste PDF URL:</p>
            <input
              type="url"
              name="resume_url"
              value={formData.resume_url}
              onChange={handleChange}
              placeholder="https://example.com/resume.pdf"
              className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:outline-none transition-all mt-2"
            />
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={submitting || uploading || uploadingAdditional || uploadingResume}
              className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-purple-500/30"
            >
              {submitting ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  SAVING...
                </>
              ) : (
                <>
                  <Save size={20} />
                  SAVE CHANGES
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin')}
              className="px-6 py-3 bg-white/5 border border-white/10 text-gray-300 rounded-lg hover:bg-white/10 transition-all"
            >
              CANCEL
            </button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminAboutEdit;